import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { ArrowLeft, TrendingUp, TrendingDown, Activity, DollarSign, Send, ArrowDownLeft } from 'lucide-react';
import Navigation from '@/components/Navigation';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CryptoAnalyticsPage = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState({
    totalSent: 0,
    totalReceived: 0,
    transactionCount: 0,
    sentCount: 0,
    receivedCount: 0,
    recentActivity: [],
    topCurrencies: [],
  });

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      const token = localStorage.getItem('routeflow_token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      // Get all transactions
      const txRes = await axios.get(`${API}/crypto-tx/history/${user.id}?limit=100`, config);
      const transactions = txRes.data;

      // Calculate analytics
      const sent = transactions.filter(tx => tx.from_user_id === user.id);
      const received = transactions.filter(tx => tx.to_user_id === user.id);

      const totalSent = sent.reduce((sum, tx) => sum + tx.amount, 0);
      const totalReceived = received.reduce((sum, tx) => sum + tx.amount, 0);

      // Currency breakdown
      const currencyMap = {};
      transactions.forEach(tx => {
        if (!currencyMap[tx.currency]) {
          currencyMap[tx.currency] = { sent: 0, received: 0, count: 0 };
        }
        if (tx.from_user_id === user.id) {
          currencyMap[tx.currency].sent += tx.amount;
        } else {
          currencyMap[tx.currency].received += tx.amount;
        }
        currencyMap[tx.currency].count++;
      });

      const topCurrencies = Object.entries(currencyMap)
        .map(([currency, data]) => ({
          currency,
          ...data,
          total: data.sent + data.received
        }))
        .sort((a, b) => b.total - a.total)
        .slice(0, 3);

      setAnalytics({
        totalSent: totalSent.toFixed(2),
        totalReceived: totalReceived.toFixed(2),
        transactionCount: transactions.length,
        sentCount: sent.length,
        receivedCount: received.length,
        recentActivity: transactions.slice(0, 5),
        topCurrencies,
      });

    } catch (error) {
      console.error('Error loading analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
        <Navigation user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
      <Navigation user={user} onLogout={onLogout} />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pt-24">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Activity & Analytics</h1>
            <p className="text-gray-600 mt-1">Track your crypto transfer activity</p>
          </div>
          <Button
            variant="ghost"
            onClick={() => navigate('/crypto/wallet')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        </div>

        {/* Overview Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardHeader className="pb-3">
              <CardDescription>Total Sent</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-gray-900">${analytics.totalSent}</p>
                  <p className="text-xs text-gray-600 mt-1">{analytics.sentCount} transactions</p>
                </div>
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardDescription>Total Received</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-gray-900">${analytics.totalReceived}</p>
                  <p className="text-xs text-gray-600 mt-1">{analytics.receivedCount} transactions</p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <TrendingDown className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardDescription>Total Transactions</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-gray-900">{analytics.transactionCount}</p>
                  <p className="text-xs text-gray-600 mt-1">All time</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <Activity className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardDescription>Net Flow</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-gray-900">
                    ${(parseFloat(analytics.totalReceived) - parseFloat(analytics.totalSent)).toFixed(2)}
                  </p>
                  <p className="text-xs text-gray-600 mt-1">
                    {parseFloat(analytics.totalReceived) > parseFloat(analytics.totalSent) ? 
                      'Positive' : 'Negative'}
                  </p>
                </div>
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top Currencies */}
          <Card>
            <CardHeader>
              <CardTitle>Top Currencies</CardTitle>
              <CardDescription>Most used currencies by transaction volume</CardDescription>
            </CardHeader>
            <CardContent>
              {analytics.topCurrencies.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No transaction data yet</p>
              ) : (
                <div className="space-y-4">
                  {analytics.topCurrencies.map((currency, index) => (
                    <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center">
                          <span className="text-white font-bold">{currency.currency[0]}</span>
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">{currency.currency}</p>
                          <p className="text-xs text-gray-600">{currency.count} transactions</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-gray-900">${currency.total.toFixed(2)}</p>
                        <div className="flex space-x-2 text-xs mt-1">
                          <span className="text-red-600">-${currency.sent.toFixed(2)}</span>
                          <span className="text-green-600">+${currency.received.toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Activity */}
          <Card>
            <CardHeader>
              <CardTitle>Recent Activity</CardTitle>
              <CardDescription>Your latest transactions</CardDescription>
            </CardHeader>
            <CardContent>
              {analytics.recentActivity.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No recent activity</p>
              ) : (
                <div className="space-y-3">
                  {analytics.recentActivity.map((tx, index) => {
                    const isSent = tx.from_user_id === user.id;
                    return (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            isSent ? 'bg-red-100' : 'bg-green-100'
                          }`}>
                            {isSent ? (
                              <Send className="w-4 h-4 text-red-600" />
                            ) : (
                              <ArrowDownLeft className="w-4 h-4 text-green-600" />
                            )}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {isSent ? 'Sent' : 'Received'}
                            </p>
                            <p className="text-xs text-gray-600">
                              {new Date(tx.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`text-sm font-semibold ${
                            isSent ? 'text-red-600' : 'text-green-600'
                          }`}>
                            {isSent ? '-' : '+'}{tx.amount} {tx.currency}
                          </p>
                          <Badge variant={tx.status === 'confirmed' ? 'success' : 'warning'} className="text-xs">
                            {tx.status}
                          </Badge>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              <Button
                onClick={() => navigate('/crypto/history')}
                variant="outline"
                className="w-full mt-4"
              >
                View All Transactions
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Insights */}
        {analytics.transactionCount > 0 && (
          <Card className="mt-6">
            <CardHeader>
              <CardTitle>Insights</CardTitle>
              <CardDescription>Based on your transaction history</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-emerald-500 rounded-full mt-2"></div>
                <p className="text-sm text-gray-700">
                  You've made <strong>{analytics.transactionCount}</strong> transactions on RouteFlow AI
                </p>
              </div>
              {analytics.sentCount > 0 && (
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full mt-2"></div>
                  <p className="text-sm text-gray-700">
                    Average transaction size: <strong>
                      ${(parseFloat(analytics.totalSent) / analytics.sentCount).toFixed(2)}
                    </strong>
                  </p>
                </div>
              )}
              {analytics.topCurrencies.length > 0 && (
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full mt-2"></div>
                  <p className="text-sm text-gray-700">
                    Your most used currency is <strong>{analytics.topCurrencies[0].currency}</strong>
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default CryptoAnalyticsPage;
